import React from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import { Add, CenterFocusStrong, Remove } from '@mui/icons-material';

import { clamp, clampTransform, MAX_SCALE, MIN_SCALE, Transform, zoomAtPoint } from '../helpers/zoomPan';

type Props = React.PropsWithChildren<{
  aspectRatio: string;
  maxWidth?: number;
}>;

const PAN_THRESHOLD = 8;
const WHEEL_ZOOM_SENSITIVITY = 0.01;

export const ZoomPan: React.FC<Props> = ({ aspectRatio, maxWidth = 560, children }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [transform, setTransform] = React.useState<Transform>({ scale: 1, x: 0, y: 0 });

  const tRef = React.useRef(transform);
  tRef.current = transform;

  const pointers = React.useRef(new Map<number, { x: number; y: number }>());
  const pinch = React.useRef<{ dist: number; mid: { x: number; y: number }; start: Transform } | null>(null);
  const pan = React.useRef<{ id: number; startX: number; startY: number; start: Transform; active: boolean } | null>(null);

  const localPoint = (clientX: number, clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    return { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) };
  };

  const clampToContainer = (t: Transform): Transform => {
    const el = containerRef.current;
    return el ? clampTransform(t, el.clientWidth, el.clientHeight) : t;
  };

  const zoomTo = (nextScale: number, cx: number, cy: number) => {
    const el = containerRef.current;
    if (!el) return;
    setTransform(prev => zoomAtPoint(prev, nextScale, cx, cy, el.clientWidth, el.clientHeight));
  };

  const centerZoom = (factor: number) => {
    const el = containerRef.current;
    if (!el) return;
    zoomTo(tRef.current.scale * factor, el.clientWidth / 2, el.clientHeight / 2);
  };

  const reset = () => setTransform({ scale: 1, x: 0, y: 0 });

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const cx = e.clientX - el.getBoundingClientRect().left;
      const cy = e.clientY - el.getBoundingClientRect().top;
      setTransform(prev =>
        zoomAtPoint(prev, prev.scale * (1 - e.deltaY * WHEEL_ZOOM_SENSITIVITY), cx, cy, el.clientWidth, el.clientHeight),
      );
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handlePointerDown = (e: React.PointerEvent) => {
    pointers.current.set(e.pointerId, localPoint(e.clientX, e.clientY));

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        start: tRef.current,
      };
      pan.current = null;
    } else if (pointers.current.size === 1 && tRef.current.scale > 1) {
      const p = localPoint(e.clientX, e.clientY);
      pan.current = { id: e.pointerId, startX: p.x, startY: p.y, start: tRef.current, active: false };
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    const point = localPoint(e.clientX, e.clientY);
    pointers.current.set(e.pointerId, point);

    if (pinch.current && pointers.current.size >= 2) {
      e.preventDefault();
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const start = pinch.current;
      const scale = clamp((start.start.scale * dist) / start.dist, MIN_SCALE, MAX_SCALE);
      const k = scale / start.start.scale;
      setTransform(
        clampToContainer({
          scale,
          x: mid.x - (start.mid.x - start.start.x) * k,
          y: mid.y - (start.mid.y - start.start.y) * k,
        }),
      );
      return;
    }

    if (pan.current && pan.current.id === e.pointerId) {
      const dx = point.x - pan.current.startX;
      const dy = point.y - pan.current.startY;
      if (!pan.current.active && Math.hypot(dx, dy) < PAN_THRESHOLD) return;
      if (!pan.current.active) {
        pan.current.active = true;
        containerRef.current?.setPointerCapture(e.pointerId);
      }
      e.preventDefault();
      setTransform(
        clampToContainer({ scale: pan.current.start.scale, x: pan.current.start.x + dx, y: pan.current.start.y + dy }),
      );
    }
  };

  const endPointer = (e: React.PointerEvent) => {
    const wasPinching = !!pinch.current;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pan.current?.id === e.pointerId) {
      if (containerRef.current?.hasPointerCapture?.(e.pointerId)) {
        containerRef.current.releasePointerCapture(e.pointerId);
      }
      pan.current = null;
    }

    if (wasPinching && pointers.current.size === 1 && tRef.current.scale > 1) {
      const [remainingId] = [...pointers.current.keys()];
      const p = pointers.current.get(remainingId);
      if (p) {
        pan.current = { id: remainingId, startX: p.x, startY: p.y, start: tRef.current, active: true };
        containerRef.current?.setPointerCapture(remainingId);
      }
    }
  };

  const zoomed = transform.scale > 1;

  return (
    <Box
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onDoubleClick={reset}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth,
        mx: 'auto',
        aspectRatio,
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        cursor: zoomed ? 'grab' : 'default',
        '&:active': { cursor: zoomed ? 'grabbing' : 'default' },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          transformOrigin: '0 0',
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
        }}
      >
        {children}
      </Box>

      <Stack
        spacing={0.5}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 3 }}
      >
        <IconButton
          size="small"
          aria-label="Zoom in"
          onClick={() => centerZoom(1.4)}
          sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}
        >
          <Add fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label="Zoom out"
          onClick={() => centerZoom(1 / 1.4)}
          sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}
        >
          <Remove fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label="Reset zoom"
          disabled={!zoomed}
          onClick={reset}
          sx={{
            bgcolor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
          }}
        >
          <CenterFocusStrong fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
};
